import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  BASE,
  GREY,
  WARNING,
} from "../../utils/constants/colors";
import { fontSize, fontWeight } from "../../utils/constants/UI";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PROFILE_PATH } from "../../utils/constants/routes";
import { CloseIcon } from "../Icon/MuiIcons";
import {
  updateBannerSeenAction,
  dismissTourBannerAction,
  getTourOnboardingStatusAction,
  markWelcomeModalSeenAction,
  resetTourOnboardingStatusAction,
} from "../../redux/actions/guidedTourActions";
import { WelcomeModal } from "../Modals/WelcomeModal";
import { useTour } from "../../context/TourContext";
import { fetchSetupStatuses } from "../../utils/helpers/setupStatusUtils";

const LEARNING_CENTER_TAB = "LearningCenter";
const TOUR_SESSION_KEY = "tourOnboardingFetched";

const isBeforeToday = (dateStr) => {
  if (!dateStr) return false;
  const lastSeenDay = new Date(dateStr).toISOString().split("T")[0];
  const todayDay = new Date().toISOString().split("T")[0];
  return lastSeenDay < todayDay;
};

const useStyles = makeStyles({
  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-100%)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },

  bannerRoot: {
    position: "fixed",
    top: 0,
    left: "5rem",
    right: 0,
    zIndex: 1100,
  },

  bannerAnimate: {
    animation: "$slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards",
  },

  bannerWrap: {
    width: "100%",
    background: WARNING.W_50,
    borderBottom: `1px solid ${WARNING.W_200}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    height: "46px",
    boxSizing: "border-box",
    gap: "16px",
  },

  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
    overflow: "hidden",
    flex: 1,
  },

  progressBadge: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "4px",
    background: WARNING.W_100,
    borderRadius: "20px",
    padding: "3px 10px",
    flexShrink: 0,
  },

  progressNum: {
    fontSize: fontSize.MD,
    fontWeight: fontWeight.BOLD,
    color: WARNING.W_700,
    lineHeight: "20px",
    letterSpacing: "-0.01em",
  },

  progressLabel: {
    fontSize: fontSize.XS,
    fontWeight: fontWeight.MEDIUM,
    color: WARNING.W_500,
    lineHeight: "20px",
  },

  vertDivider: {
    width: "1px",
    height: "18px",
    background: WARNING.W_200,
    flexShrink: 0,
  },

  copyText: {
    fontSize: fontSize.SM,
    fontWeight: fontWeight.REGULAR,
    color: GREY.SECONDARY,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },

  learnBtn: {
    display: "flex",
    alignItems: "center",
    backgroundColor: WARNING.W_700,
    color: BASE.WHITE,
    border: "none",
    borderRadius: "7px",
    padding: "6px 14px",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.SEMIBOLD,
    lineHeight: "20px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    letterSpacing: "0.01em",
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: WARNING.W_800 },
  },

  dismissBtn: {
    backgroundColor: "transparent",
    color: GREY.TERTIARY,
    border: "none",
    padding: "4px 2px",
    fontSize: fontSize.XS,
    fontWeight: fontWeight.REGULAR,
    lineHeight: "18px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    transition: "color 0.15s ease",
    "&:hover": {
      color: GREY.SECONDARY,
    },
  },

  closeBtn: {
    backgroundColor: "transparent",
    border: "none",
    color: GREY.TERTIARY,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "4px",
    borderRadius: "4px",
    marginLeft: "4px",
    transition: "color 0.15s ease, background 0.15s ease",
    "&:hover": { backgroundColor: WARNING.W_100, color: WARNING.W_700 },
  },
});

const GuidedTourBanner = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { openSkipModal } = useTour();
  const hasFetchedRef = useRef(false);
  const hasFetchedSetupRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [permanentlyDismissed, setPermanentlyDismissed] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [setupStatuses, setSetupStatuses] = useState(null); // null = not yet loaded

  const setupDone = useSelector(
    (state) => !!state?.userDetails?.payload?.general_setting_setup,
  );
  const onboardingStatus = useSelector((state) => state?.tourOnboardingStatus);
  const { fetched, data } = onboardingStatus || {};

  // Step 1: fetch onboarding status once setup is complete
  useEffect(() => {
    if (!setupDone) return;
    if (hasFetchedRef.current) return;
    if (localStorage.getItem(TOUR_SESSION_KEY)) return;

    hasFetchedRef.current = true;
    dispatch(resetTourOnboardingStatusAction());

    (async () => {
      const result = await dispatch(getTourOnboardingStatusAction(navigate));
      if (!result?.success) return;
      localStorage.setItem(TOUR_SESSION_KEY, "true");
      if (result.data?.seen_welcome_modal === false) {
        setWelcomeOpen(true);
        dispatch(markWelcomeModalSeenAction(navigate));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupDone]);

  // New-user path: ProfileForm sets this flag after a successful step-2 submit,
  // just before navigating to the dashboard. The setupDone guard above can't fire
  // here because Redux userDetails hasn't been updated yet, so we handle it
  // independently. The flag is cleared immediately so it never triggers again.
  useEffect(() => {
    const justDone = localStorage.getItem("riq_just_completed_onboarding");
    if (!justDone) return;
    localStorage.removeItem("riq_just_completed_onboarding");

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    dispatch(resetTourOnboardingStatusAction());

    (async () => {
      const result = await dispatch(getTourOnboardingStatusAction(navigate));
      if (!result?.success) return;
      localStorage.setItem(TOUR_SESSION_KEY, "true");
      if (result.data?.seen_welcome_modal === false) {
        setWelcomeOpen(true);
        dispatch(markWelcomeModalSeenAction(navigate));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-conditions: everything except setup statuses and composite % check.
  // When these are true we know we MIGHT need to show the banner — fetch setup statuses.
  const bannerPreConditions =
    fetched &&
    data !== null &&
    data?.seen_welcome_modal === true &&
    data?.hide_tour_banner === false &&
    isBeforeToday(data?.last_seen_banner_at) &&
    data?.progress_numerator !== null &&
    data?.progress_denominator !== null;

  // Step 2: once pre-conditions are met, fetch the 4 setup statuses.
  // Only run once — hasFetchedSetupRef guards against repeat calls.
  useEffect(() => {
    if (!bannerPreConditions) return;
    if (hasFetchedSetupRef.current) return;
    hasFetchedSetupRef.current = true;
    fetchSetupStatuses().then(setSetupStatuses);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerPreConditions]);

  // Composite progress: (tour_num + setup_done) / (tour_den + 4) * 100
  const doneSetupCount = setupStatuses
    ? Object.values(setupStatuses).filter(Boolean).length
    : 0;
  const compositeNum = (data?.progress_numerator ?? 0) + doneSetupCount;
  const compositeDen = (data?.progress_denominator ?? 0) + 4;
  const compositePct = compositeDen > 0
    ? Math.round((compositeNum / compositeDen) * 100)
    : 0;

  // Step 3: banner is ready to show — all data is loaded, percentage is accurate.
  const shouldShowBanner =
    bannerPreConditions &&
    setupStatuses !== null &&   // setup check is complete
    compositePct < 100;

  useEffect(() => {
    if (shouldShowBanner) {
      setVisible(true);
      setAnimate(true);
      dispatch(updateBannerSeenAction(navigate));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowBanner]);

  const isOnLearningCenter =
    location.pathname.startsWith(PROFILE_PATH) &&
    location.search.includes(`tab=${LEARNING_CENTER_TAB}`);

  useEffect(() => {
    if (isOnLearningCenter) {
      setPermanentlyDismissed(true);
    }
  }, [isOnLearningCenter]);

  const handleGoToLearningCenter = () => {
    setPermanentlyDismissed(true);
    navigate(`${PROFILE_PATH}?tab=${LEARNING_CENTER_TAB}`);
  };

  const handleNeverShow = () => {
    dispatch(dismissTourBannerAction(navigate));
    setPermanentlyDismissed(true);
  };

  return (
    <>
      <WelcomeModal
        open={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        onExploreSelf={() => { setWelcomeOpen(false); openSkipModal(); }}
      />
      {/* RIBBON RETIRED (2026-09-22, per Aditya): the yellow "guided tour / % explored"
          bar never renders — but this component stays mounted because the WelcomeModal
          ("Welcome to ReachIQ" popup for new users) lives here and must keep working. */}
      {false && visible && !permanentlyDismissed && ReactDOM.createPortal(
        <Box
          className={`${classes.bannerRoot}${animate ? ` ${classes.bannerAnimate}` : ""}`}
          onAnimationEnd={() => setAnimate(false)}
        >
          <Box className={classes.bannerWrap}>
            {/* Left: progress pill + divider + copy */}
            <Box className={classes.leftSection}>
              <Box className={classes.progressBadge}>
                <span className={classes.progressNum}>{compositePct}%</span>
                <span className={classes.progressLabel}>explored</span>
              </Box>
              <Box className={classes.vertDivider} />
              <span className={classes.copyText}>
                Get the most out of ReachIQ. Start a guided tour.
              </span>
            </Box>

            {/* Right: never show + CTA + close (CTA next to close = more visible when reaching for X) */}
            <Box className={classes.rightSection}>
              <button className={classes.dismissBtn} onClick={handleNeverShow}>
                Never show again
              </button>
              <button className={classes.learnBtn} onClick={handleGoToLearningCenter}>
                Open Learning Center
              </button>
              <button
                className={classes.closeBtn}
                onClick={() => setVisible(false)}
                title="Dismiss"
              >
                <CloseIcon style={{ fontSize: "14px" }} />
              </button>
            </Box>
          </Box>
        </Box>,
        document.body,
      )}
    </>
  );
};

export default GuidedTourBanner;